import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ConfigurationResultService } from '../../services/configuration-result.service';
import { Product, ProductsService } from '../../services/products.service';
import { Quote, QuoteService } from '../../services/quote.service';
import { ConfigitConfigurationAndProducts } from '../questionnaire/configit-quest-adapter/configit-types.interface';

export enum ProductEditType {
    optional = 'optional',
}

export interface ProductEditEvent {
    type: ProductEditType;
    productId: string;
    newValue?: number | boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ProductListService {
    public isProductListLoading$ = new BehaviorSubject<boolean>(false);
    public productList$: Observable<Product[]>;
    public productListEdits$ = new BehaviorSubject<ProductEditEvent[]>([]);

    constructor(
        private quoteService: QuoteService,
        private configurationResultService: ConfigurationResultService,
        private productService: ProductsService
    ) {
        this.productList$ = combineLatest([
            this.configurationResultService.configuration$.pipe(
                distinctUntilChanged(
                    (prev, current) => JSON.stringify(prev?.bomItems) === JSON.stringify(current?.bomItems)
                )
            ),
            this.productListEdits$,
        ]).pipe(
            tap(() => this.isProductListLoading$.next(true)),
            switchMap(([configuration, editEvents]) => {
                const quote = this.quoteService.getCurrentQuote();
                if (!quote || !configuration) {
                    return of(undefined);
                }
                return forkJoin([this.getProductsFromConfiguration(configuration, quote), of(editEvents)]);
            }),
            catchError((error) => {
                this.isProductListLoading$.next(false);
                return throwError(() => error);
            }),
            filter(Boolean),
            map(([products, editEvents]) => this.applyEditEvents(products, editEvents)),
            tap(() => this.isProductListLoading$.next(false)),
            shareReplay(1)
        );
    }

    public clearEdits() {
        this.productListEdits$.next([]);
    }

    private applyEditEvents(products: Product[], events: ProductEditEvent[]): Product[] {
        return products.map((product) =>
            events
                .filter((e) => e.productId === product.material)
                .reduce((p, event) => {
                    switch (event.type) {
                        case ProductEditType.optional: {
                            return { ...p, optional: <boolean>event.newValue };
                        }
                    }
                    return p;
                }, product)
        );
    }

    private getProductsFromConfiguration(
        configuration: ConfigitConfigurationAndProducts,
        quote: Quote
    ): Observable<Product[]> {
        const products = configuration.bomItems.filter((item) => item.componentQuantity > 0);

        if (products.length === 0) {
            return of([]);
        }

        this.quoteService.pruneProductItems(products.map((p) => p.material));

        return this.productService.getProducts(
            products.map((p) => ({ material: p.material, quantity: p.componentQuantity, bomItemId: p.itemId })),
            quote
        );
    }
}
